import type { LandingProductCopy } from './landingProductCopy';
import type {
  LandingProductCallbacks,
  LandingProductSnapshot,
} from './landingProductDescriptor.types';
import {
  createFmProductShowcaseMapMedia,
  type FmCopyElement,
  type FmKeyFigure,
  type FmKeyFiguresElement,
  type FmProductSectionDescriptor,
} from '@/components/ui/archetypes';
import type { Language } from '@/i18n';
import { getSpeciesName, servedStandSpeciesCode } from '@/types/generated/forest_models';

// Public stand properties are sparse by contract; each optional field is guarded independently.
// eslint-disable-next-line complexity
function selectedStandDetails(
  copy: LandingProductCopy,
  snapshot: Readonly<LandingProductSnapshot>,
): {
  description: string;
  facts: FmKeyFigure[];
} {
  const selectedId = snapshot.showcaseSelectedStandId;
  const feature = selectedId
    ? snapshot.showcaseMapData?.visualizationData?.features.find(
        (candidate) =>
          String(
            candidate.properties?.stand_id ?? candidate.properties?.standId ?? candidate.id,
          ) === selectedId,
      )
    : undefined;
  if (!feature) {
    return {
      description: copy.showcase.mapDescription,
      facts: [
        standCountFigure(copy, snapshot),
        {
          label: copy.showcase.mapSelectedLabel,
          value: null,
          unavailableReason: copy.showcase.mapSelectedHint,
        },
      ],
    };
  }
  const properties = feature.properties ?? {};
  const number = String(properties.stand_number ?? properties.STANDNUMBER ?? selectedId);
  const details = [`${copy.showcase.mapSelectedLabel} ${number}`];
  const area = properties.area_hectares;
  if (typeof area === 'number' && Number.isFinite(area)) {
    details.push(
      `${new Intl.NumberFormat(snapshot.locale, { maximumFractionDigits: 1 }).format(area)} ha`,
    );
  }
  const speciesCode = servedStandSpeciesCode(properties);
  const speciesName = speciesCode === null ? null : getSpeciesName(speciesCode, snapshot.locale);
  if (speciesName !== null) {
    details.push(speciesName);
  }
  const sourceDate = properties.data_date;
  if (typeof sourceDate === 'string' && sourceDate.trim()) {
    details.push(`${copy.showcase.mapSelectedSource} ${sourceDate}`);
  }
  // The selected stand's number, species and source date read in the
  // description; the figures carry only numbers: the stand count and its area.
  const factRows: FmKeyFigure[] = [standCountFigure(copy, snapshot)];
  if (typeof area === 'number' && Number.isFinite(area)) {
    factRows.push({
      label: copy.labels.area,
      value: new Intl.NumberFormat(snapshot.locale, { maximumFractionDigits: 1 }).format(area),
      unit: 'ha',
      ...(speciesName !== null ? { footnote: speciesName } : {}),
    });
  }
  return {
    description: `${details.join(' · ')}. ${copy.showcase.mapSelectedHint}`,
    facts: factRows,
  };
}

/** The served stand count, or its explicit absence while no map data is served. */
function standCountFigure(
  copy: LandingProductCopy,
  snapshot: Readonly<LandingProductSnapshot>,
): FmKeyFigure {
  const count = snapshot.showcaseMapData?.visualizationData?.features.length;
  return count === undefined
    ? { label: copy.labels.stands, value: null, unavailableReason: copy.state.empty }
    : {
        label: copy.labels.stands,
        value: String(count),
        footnote: copy.showcase.mapTags[0],
      };
}

// The showcase keeps live-map, valuation-coverage, and unavailable-data branches explicit.
// eslint-disable-next-line complexity
export function featureShowcaseSection(
  copy: LandingProductCopy,
  snapshot: Readonly<LandingProductSnapshot>,
  callbacks: LandingProductCallbacks,
): FmProductSectionDescriptor {
  const selected = selectedStandDetails(copy, snapshot);
  const propertyId =
    snapshot.showcaseMapData?.activePropertyId ??
    snapshot.story?.firstPicture.identity.displayPropertyId;
  const storyPropertyId = snapshot.story?.firstPicture.identity.displayPropertyId;
  const valueCoverage =
    propertyId && storyPropertyId === propertyId
      ? snapshot.story?.firstPicture.roughValueRange
      : undefined;
  const mapStandCount = snapshot.showcaseMapData?.visualizationData?.features.length;
  const totalStands = mapStandCount ?? valueCoverage?.totalStandCount;
  const resultCopy: Record<
    Language,
    { found: string; stands: string; coverage: string; coveragePending: string; reveal: string }
  > = {
    fi: {
      found: 'Löysimme metsätilan',
      stands: 'kuviota',
      coverage: 'Hinta-arvion kattamat kuviot',
      coveragePending: 'Näe arvo ja tietopohja',
      reveal: 'Näytä hinta-arvion perusteet',
    },
    en: {
      found: 'We found the forest property',
      stands: 'stands',
      coverage: 'Stands covered by the price estimate',
      coveragePending: 'See value and evidence',
      reveal: 'See the valuation basis',
    },
    sv: {
      found: 'Vi hittade skogsfastigheten',
      stands: 'bestånd',
      coverage: 'Bestånd som omfattas av prisbedömningen',
      coveragePending: 'Se värde och underlag',
      reveal: 'Se värderingsunderlaget',
    },
    es: {
      found: 'Encontramos la finca forestal',
      stands: 'rodales',
      coverage: 'Rodales cubiertos por la estimación',
      coveragePending: 'Ver valor y datos',
      reveal: 'Ver la base de valoración',
    },
  };
  const result = resultCopy[snapshot.locale];
  const resultSummary =
    propertyId && totalStands
      ? `${result.found} ${propertyId}: ${totalStands} ${result.stands}. ${selected.description}`
      : selected.description;
  const matchingCoverage =
    valueCoverage &&
    storyPropertyId === propertyId &&
    valueCoverage.totalStandCount > 0 &&
    valueCoverage.totalStandCount === mapStandCount;
  const resultFacts: FmKeyFigure[] = valueCoverage
    ? [
        ...selected.facts,
        matchingCoverage
          ? {
              label: result.coverage,
              value: `${valueCoverage.valuedStandCount}/${valueCoverage.totalStandCount}`,
              footnote: copy.valueStreams.value.title,
            }
          : {
              label: result.coverage,
              value: null,
              unavailableReason: result.coveragePending,
              footnote: copy.valueStreams.value.title,
            },
      ]
    : selected.facts;
  const identity = snapshot.showcaseMapData?.activePropertyId;
  const lead: FmCopyElement = {
    element: 'copy',
    heading: copy.showcase.mapTitle,
    lede: resultSummary,
    badges: [
      ...(identity ? [{ label: identity, tone: 'accent' as const }] : []),
      { label: copy.labels.sources, tone: 'neutral' as const },
    ],
  };
  const [firstFact, ...restFacts] = resultFacts;
  const figures: FmKeyFiguresElement = { element: 'keyFigures', figures: [firstFact, ...restFacts] };
  const content = [
    lead,
    figures,
    {
      element: 'actions' as const,
      secondary: [{ label: result.reveal, href: '#landing-owner-valuation' }] as const,
    },
  ] as const;
  const section = {
    id: 'landing-feature-map-story',
    state: { status: 'ready' as const },
    testId: 'landing-feature-map',
  };
  return {
    id: 'landing-feature-showcase',
    capability: 'section',
    role: 'lead',
    title: copy.understand.sectionTitle,
    section: snapshot.showcaseMapData
      ? {
          ...section,
          arrangement: 'split',
          balance: 'copy-narrow',
          slots: {
            content,
            media: [
              createFmProductShowcaseMapMedia(
                snapshot.showcaseMapData,
                snapshot.showcaseSelectedStandId,
                callbacks.selectShowcaseStand,
              ),
            ],
          },
        }
      : { ...section, arrangement: 'prose', slots: { content } },
  };
}
